"use client"

import { useState } from 'react'
import { Navigate, useLocation, Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import AuthLayout from './AuthLayout'
import { useLogin, useAuth } from '../../lib/api'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const loginMutation = useLogin()

  const from = (location.state as any)?.from?.pathname || '/admin'

  console.log('🔍 Login component state:', { isAuthenticated, from })

  if (isAuthenticated) {
    console.log('✅ User is authenticated, redirecting to:', from)
    return <Navigate to={from} replace />
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    console.log("🚀 Starting login process")
    loginMutation.mutate(
      { emailAddress: email, password },
      {
        onSuccess: (response) => {
          console.log("📝 Full login response:", response)
          console.log("🔑 Response requires2FA:", response.requires2FA)

          if (response.success) {
            console.log("✅ Login successful")

            if (response.requires2FA) {
              // 2FA disabled - navigate to confirm login page
              console.log("🔐 2FA required - navigating to confirm login")
              navigate('/confirm-login', {
                state: {
                  from: location.state || { pathname: from },
                  email: email
                },
                replace: true
              })
            } else {
              // 2FA enabled - user is fully authenticated, navigate
              console.log("🚀 User fully authenticated, about to navigate to:", from)
              console.log("🏪 Checking sessionStorage for auth data...")
              console.log("Token:", sessionStorage.getItem('auth_token'))
              console.log("User:", sessionStorage.getItem('current_user'))

              // Small delay to ensure auth state updates
              setTimeout(() => {
                console.log("🔄 Navigating now...")
                navigate(from, { replace: true })
              }, 100)
            }
          }
        },
        onError: (error) => {
          console.error("❌ Login error:", error)
        }
      }
    )
  }


  return (
    <AuthLayout>
      {/* Form Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2 font-satoshi">
          Sign In To Your Account
        </h2>
        <p className="text-sm sm:text-base text-gray-600 font-satoshi">
          Control & Manage your website
        </p>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
          {loginMutation.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-satoshi">
              {loginMutation.error.message || 'An error occurred during login'}
            </div>
          )}

        {/* Email Field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e.g. johndoe@gmail.com"
            className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi text-sm sm:text-base"
            required
          />
        </div>

        {/* Password Field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2 font-satoshi">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter"
              className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 sm:pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi text-sm sm:text-base"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
          </div>
        </div>

        {/* Forgot Password Link */}
        <div className="text-right">
          <Link to="/forgot-password" className="text-teal-600 hover:text-teal-700 text-sm font-medium font-satoshi">
            Forgot Password?
          </Link>
        </div>

        {/* Sign In Button */}
        <button
          type="submit"
          disabled={loginMutation.isPending || !password || !email}
          className={`w-full font-medium py-2.5 sm:py-3 px-4 rounded-lg transition-colors font-satoshi disabled:bg-gray-400 text-sm sm:text-base ${
            loginMutation.isPending
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-forestGreen-100 hover:bg-green-700 text-white'
          }`}
        >
          {loginMutation.isPending ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Signing In...
            </div>
          ) : (
            'Sign In'
          )}
        </button>
      </form>
    </AuthLayout>
  )
}

export default Login